import type { Report } from '@/models/report';
import type { Study } from '@/models/study';
import { ageInYears } from '@/shared/utils/patientAge';
import { formatStudyDateTime } from '@/shared/utils/studyDate';
import { formatDate } from '../../utils/formatDateTime';
import styles from './PatientInfoSection.module.css';

interface PatientInfoSectionProps {
  study: Study | undefined;
  report: Report | undefined;
}

function KV({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}) {
  return (
    <div className={styles.kv}>
      <span className={styles.kvLabel}>{label}:</span>
      <span className={styles.kvValue}>{value || '—'}</span>
    </div>
  );
}

// Age at the study date, the same age the reference ranges are graded
// against — not the patient's age today.
function computeAge(
  dob?: string | null,
  studyDate?: string | null
): string | undefined {
  const years = ageInYears(dob, studyDate);
  return years === null ? undefined : `${years}`;
}

function joinNames(
  first?: string | null,
  last?: string | null
): string | undefined {
  const full = `${first ?? ''} ${last ?? ''}`.trim();
  return full || undefined;
}

function formatNumber(val?: number | null, unit?: string): string | undefined {
  if (val == null) return undefined;
  return unit ? `${val} ${unit}` : `${val}`;
}

export default function PatientInfoSection({
  study,
  report,
}: PatientInfoSectionProps) {
  const patientName = joinNames(
    study?.patientFirstName,
    study?.patientLastName
  );
  const referringMd = joinNames(
    study?.referringPhysicianFirstName,
    study?.referringPhysicianLastName
  );
  const sonographer = joinNames(
    study?.sonographerFirstName,
    study?.sonographerLastName
  );
  const readingMd = report?.signature
    ? joinNames(
        report.signature.signerFirstName,
        report.signature.signerLastName
      )
    : undefined;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Patient Information</h3>
      <div className={styles.grid}>
        <KV label="Name" value={patientName} />
        <KV label="DOB" value={formatDate(study?.patientDateOfBirth)} />
        <KV
          label="Age"
          value={computeAge(study?.patientDateOfBirth, study?.studyDate)}
        />
        <KV label="Sex" value={study?.patientSex} />
        <KV label="Height" value={formatNumber(study?.height, 'cm')} />
        <KV label="Weight" value={formatNumber(study?.weight, 'kg')} />
        <KV label="BSA" value={formatNumber(study?.bodySurfaceArea, 'm²')} />
        <KV label="BP" value={study?.bloodPressure} />
        <KV label="Study Date" value={formatStudyDateTime(study?.studyDate)} />
        <KV label="MRN" value={study?.patientMedicalRecordNumber} />
        <KV label="Encounter #" value={study?.encounterNumber} />
        <KV label="Referring MD" value={referringMd} />
        <KV label="Sonographer" value={sonographer} />
        <KV label="Equipment" value={study?.manufacturer} />
        <KV label="Location" value={study?.site} />
        <KV label="Reading MD" value={readingMd} />
      </div>
    </section>
  );
}
